import React from 'react';
import { Button, Dropdown } from 'antd';
import { BellFilled, CloseOutlined } from '@ant-design/icons';
import { formatReminderTime, parseRemindAt } from '../../data/workOrderStarLabels';
import { WorkOrderStarChangeMessage } from '../../hooks/useWorkOrderStarSocket';
import connectStarActions, { StarActionProps } from './connectStarActions';
import useStarActions, { StarSnapshot, WorkOrderToastLink } from './useStarActions';
import useReminderSnoozeControls from './useReminderSnoozeControls';
import OWButton from '../ow_button/OWButton';

interface OwnProps {
	reminder: WorkOrderStarChangeMessage;
	// The reminder's saved condition, kept when snoozing from the card.
	condition?: string | null;
	// Called once the reminder is snoozed or stopped, to take the card off the stack.
	onDone: () => void;
	// Opening the work order or closing the card: marks the reminder's notification read and takes
	// the card off the stack. The reminder itself keeps ringing on the work order.
	onRead: () => void;
}

type Props = OwnProps & StarActionProps;

/**
 * One due reminder in the top-right stack: "Reminder 4:30 PM for WO #123 ↗", the tracking note,
 * and SNOOZE / STOP. Both write through the same star actions as everywhere else, so the usual
 * toast (with UNDO) follows. Opening the work order or the × reads the notification and closes the
 * card, leaving the reminder ringing.
 */
const ReminderAlertCard: React.FC<Props> = (props) => {
	const { reminder, condition = null, onDone, onRead, canAddNote, roleType } = props;
	const remindAt = parseRemindAt(reminder.remindAt);
	const note = reminder.starNote || null;
	const current: StarSnapshot = { note, remindAt, condition };
	const { isPersisting, snooze, stop } = useStarActions(reminder.workOrderId, props);

	const doneIfSaved = (saved: boolean) => {
		if (saved) onDone();
		return saved;
	};
	const { snoozeMenu, customSnoozeModal } = useReminderSnoozeControls({
		isPersisting,
		snoozeUntil: (until) => snooze(current, until).then(doneIfSaved),
	});

	return (
		<div className="pointer-events-auto flex w-[400px] items-start gap-m rounded-sm border border-solid border-grey-4 bg-white p-m shadow-notifications-card">
			<span className="box-border inline-flex h-10 w-10 shrink-0 animate-reminderGlow items-center justify-center rounded-full bg-character-danger">
				<BellFilled className="!flex origin-top animate-bellRing text-base !text-white" />
			</span>
			<div className="flex min-w-0 flex-1 flex-col gap-xxs pt-xs">
				<span className="h5regular text-character-primary">
					{`Reminder ${remindAt ? `${formatReminderTime(remindAt)} ` : ''}for `}
					<WorkOrderToastLink
						workOrderId={reminder.workOrderId}
						roleType={roleType}
						onClick={onRead}
					/>
				</span>
				{note ? (
					<span className="bodyregular break-words text-character-secondary">{note}</span>
				) : null}
				<div className="flex justify-end gap-xs pt-s">
					{/* Snoozing sets a new reminder time, so it's Pro-only like adding one. */}
					{canAddNote ? (
						<Dropdown overlay={snoozeMenu} trigger={['click']} disabled={isPersisting}>
							<Button disabled={isPersisting}>SNOOZE</Button>
						</Dropdown>
					) : null}
					<OWButton
						type="primary"
						onClick={() => stop(current).then(doneIfSaved)}
						loading={isPersisting}
					>
						STOP
					</OWButton>
				</div>
			</div>
			<Button
				type="text"
				size="small"
				aria-label="Dismiss"
				title="Dismiss"
				icon={<CloseOutlined />}
				onClick={onRead}
				className="-mr-xs -mt-xs shrink-0 text-character-secondary"
			/>
			{customSnoozeModal}
		</div>
	);
};

export default connectStarActions(ReminderAlertCard as any) as React.ComponentType<OwnProps>;
